import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ApartmentOutlined,
  KeyboardArrowDownRounded,
  PersonOutlineRounded,
} from '@mui/icons-material';
import { step1schema, step4Schema, step5Schema } from './validationSchemas';
import { PHONE_MIN_LENGTH, PHONE_MAX_LENGTH } from '../../constants/phone';
import { getCountryMetaData } from './login.service';
import {
  validationApi,
  validateEmailApi,
  eklinkSignupVerifyOtp,
  eklinkSignupSendOtp,
} from '../../services/authServices';
import { CountryPhoneCodeMenuOption, CountryPhoneCodeOption } from './countryFlag';
import { useSignupMetaData } from '../hooks/useSignupMetaData';
import signupFlowSteps from './signupFlowConfig';
import SignupProgress from './signupProgress';
import { authHeadingStyles, authSubtextStyles } from './signupTheme';
import {
  SCORE_CUSTOMER_TYPES,
  isScoreCustomerPersona,
  signupPersonaLabel,
} from './scoreCustomerSignup';

const baseUrl = process.env.REACT_APP_AZURE_IMAGE_BASE_URL;
const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;
const OTP_LENGTH = 4;
const otpRowWidth = {
  xs: `calc(${OTP_LENGTH} * 42px + ${(OTP_LENGTH - 1) * 16}px)`,
  sm: `calc(${OTP_LENGTH} * 52px + ${(OTP_LENGTH - 1) * 16}px)`,
};

const registrationOptions = [
  {
    value: 'individual',
    label: 'Individual',
    icon: <PersonOutlineRounded fontSize="small" />,
  },
  {
    value: 'organization',
    label: 'Organization',
    icon: <ApartmentOutlined fontSize="small" />,
  },
];

const serviceProviderTypeOptions = [
  { value: 'type1', label: 'Type 1' },
  { value: 'type2', label: 'Type 2' },
  { value: 'type3', label: 'Type 3' },
  { value: 'type4', label: 'Type 4' },
];

const fieldLabelStyles = {
  color: 'text.primary',
  fontWeight: 700,
};

const fieldGroupStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const formatPersonaTypeName = (typeName) => {
  if (!typeName) return typeName;
  const upperTypeName = typeName.toUpperCase();
  if (upperTypeName === 'DARE' || upperTypeName === 'NGO' || upperTypeName === 'FPO') return upperTypeName;
  return typeName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const buildLinkUrl = (path) => {
  if (!path || !baseUrl || !sasToken) {
    return '';
  }

  return `${baseUrl}/${path}?${sasToken}`;
};

const buildIconUrl = (iconUrl) => {
  if (!iconUrl || !sasToken) {
    return '';
  }

  return `${iconUrl}?${sasToken}`;
};

const RoleIcon = ({ iconUrl }) => {
  const [failed, setFailed] = useState(false);
  const resolvedUrl = buildIconUrl(iconUrl);
  const showImage = resolvedUrl && !failed;

  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '6px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        bgcolor: showImage ? 'transparent' : 'action.hover',
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={resolvedUrl}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          onError={() => setFailed(true)}
        />
      ) : (
        <PersonOutlineRounded sx={{ fontSize: 16, color: 'text.disabled' }} />
      )}
    </Box>
  );
};

export default function SignupStep1({ handleContinue, handleChange, formData, preselectPersona = null }) {
  const navigate = useNavigate();
  const personaPreselected = useRef(false);
  const { userTypes, countryList } = useSignupMetaData(formData.administrativeRegion);
  const [countryOptions, setCountryOptions] = useState([]);
  const [registrationType, setRegistrationType] = useState(
    formData.registrationType || (formData.organisationName ? 'organization' : 'individual')
  );
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showOtp, setShowOtp] = useState(false);
  const [otpInputsVisible, setOtpInputsVisible] = useState(false);
  const [otp, setOtp] = useState(() => Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // Closing keeps the message and severity, so the fade-out shows this alert, not an empty
  // blue 'info' one.
  const closeFeedback = () => setFeedback((current) => current && { ...current, open: false });
  const [timerSeconds, setTimerSeconds] = useState(0);

  const availableUserTypes = useMemo(() => userTypes || [], [userTypes]);
  const selectedUserType = useMemo(
    () =>
      availableUserTypes.find(
        (userType) => String(userType.id) === String(formData.userTypeId)
      ) || null,
    [availableUserTypes, formData.userTypeId]
  );
  const isScoreCustomer = isScoreCustomerPersona(selectedUserType?.typeName);

  // ?persona=SCORE_CUSTOMER (e.g. the eK Score Marketplace's Register button): once the chosen
  // country's personas arrive, select that persona if the country offers it. Applied once per
  // country, and never over a role the user already picked.
  useEffect(() => {
    if (!preselectPersona || formData.userTypeId || !availableUserTypes.length) return;
    if (personaPreselected.current === formData.administrativeRegion) return;
    personaPreselected.current = formData.administrativeRegion;
    const match = availableUserTypes.find(
      (userType) => String(userType.typeName || '').trim().toUpperCase() === preselectPersona
    );
    if (match) {
      handleChange({ target: { name: 'userTypeId', value: String(match.id) } });
      handleChange({ target: { name: 'personaName', value: match.typeName } });
    }
  }, [preselectPersona, availableUserTypes, formData.userTypeId, formData.administrativeRegion, handleChange]);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getCountryMetaData();
        if (response.statusCode === 200) {
          // Store full country data including addressTemplate
          setCountryOptions(response.data);
        } else {
          setCountryOptions([]);
        }
      } catch (error) {
        setCountryOptions([]);
      }
    };

    loadCountries();
  }, []);

  useEffect(() => {
    if (
      selectedUserType?.typeName !== 'Service Provider' &&
      formData.serviceProviderType
    ) {
      handleChange({
        target: { name: 'serviceProviderType', value: '' },
      });
    }
  }, [formData.serviceProviderType, handleChange, selectedUserType]);

  useEffect(() => {
    if (timerSeconds <= 0) return;

    const interval = setInterval(() => {
      setTimerSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [timerSeconds]);

  const termsUrl = '/TermsOfUse_ALLIN.pdf';
  const privacyUrl = buildLinkUrl(countryList?.privacyPolicy);
  const feesUrl = buildLinkUrl(
    countryList?.fees ||
      countryList?.fee ||
      countryList?.feeDetails ||
      countryList?.feePolicy ||
      countryList?.feesPolicy ||
      countryList?.feesAndCharges
  );

  const setFieldValue = (name, value, type = 'text') => {
    handleChange({
      target: {
        name,
        value,
        checked: type === 'checkbox' ? value : undefined,
        type,
      },
    });
  };

  const buildValidationModel = (overrides = {}) => {
    const nextModel = { ...formData, ...overrides };
    const resolvedUserTypeName =
      overrides.selectedUserTypeName ||
      availableUserTypes.find(
        (userType) => String(userType.id) === String(nextModel.userTypeId)
      )?.typeName;

    return {
      ...nextModel,
      registrationType:
        overrides.registrationType || nextModel.registrationType || registrationType,
      userType: resolvedUserTypeName,
    };
  };

  const showFeedback = (severity, message) => {
    setFeedback({ severity, message, open: true });
  };

  const setFieldTouched = (name) => {
    setTouched((currentTouched) => ({ ...currentTouched, [name]: true }));
  };

  const setFieldError = (name, message) => {
    setErrors((currentErrors) => ({ ...currentErrors, [name]: message }));
  };

  const clearFieldError = (name) => {
    setErrors((currentErrors) => {
      if (!currentErrors[name]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[name];
      return nextErrors;
    });
  };

  const validateField = async (name, value, overrides = {}) => {
    try {
      await step1schema.validateAt(
        name,
        buildValidationModel({ ...overrides, [name]: value })
      );

      clearFieldError(name);
    } catch (error) {
      setFieldError(name, error.message);
    }
  };

  const validateStep = async () => {
    const nextErrors = {};

    if (!formData.otpVerified) {
      nextErrors.phoneNumber = 'Verify your phone number before continuing.';
    }

    try {
      await step1schema.validate(buildValidationModel(), {
        abortEarly: false,
      });
    } catch (validationError) {
      validationError.inner.forEach((error) => {
        if (!nextErrors[error.path]) {
          nextErrors[error.path] = error.message;
        }
      });
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleRegistrationChange = (value) => {
    setRegistrationType(value);
    setFieldValue('registrationType', value);
    setFieldTouched('registrationType');
    clearFieldError('registrationType');

    if (value === 'organization') {
      setFieldValue('firstName', '');
      setFieldValue('lastName', '');
      clearFieldError('firstName');
      clearFieldError('lastName');
    } else {
      setFieldValue('organisationName', '');
      clearFieldError('organisationName');
    }
  };

  const handleTextFieldChange = (name) => (event) => {
    const value = event.target.value;
    setFieldValue(name, value);
    setFieldTouched(name);
    validateField(name, value);
  };

  const handleCountryChange = (event) => {
    const value = event.target.value || '';
    const selectedCountry = countryOptions.find(
      (country) => String(country.countryManagement.id) === String(value)
    );

    setFieldValue('administrativeRegion', value);
    setFieldValue('userTypeId', '');
    setFieldValue('personaName', '');
    setFieldValue('serviceProviderType', '');
    setFieldValue('scoreCustomerType', '');
    // Store address template for use in Step 2
    if (selectedCountry?.addressTemplate) {
      setFieldValue('addressTemplate', selectedCountry.addressTemplate);
    }
    setFieldTouched('administrativeRegion');
    validateField('administrativeRegion', value);

    if (selectedCountry?.countryManagement?.phoneCode) {
      setFieldValue('countryCode', String(selectedCountry.countryManagement.phoneCode));
      clearFieldError('countryCode');
    }

    // Switching country changes the accepted phone length, so clear any entered
    // phone number and reset the OTP / verification state that depended on it.
    if (formData.phoneNumber) {
      setFieldValue('phoneNumber', '');
      clearFieldError('phoneNumber');
    }
    setShowOtp(false);
    setOtpInputsVisible(false);
    setOtp(Array(OTP_LENGTH).fill(''));
    setTimerSeconds(0);

    if (formData.otpVerified) {
      setFieldValue('otpVerified', false);
      setFieldValue('termsAndPolicies', false, 'checkbox');
    }
  };

  const handleRoleChange = (event) => {
    const roleId = event.target.value || '';
    const role = availableUserTypes.find(
      (userType) => String(userType.id) === String(roleId)
    );

    setFieldValue('userTypeId', roleId);
    setFieldValue('personaName', role?.typeName || '');
    setFieldTouched('userTypeId');

    if (!isScoreCustomerPersona(role?.typeName)) {
      setFieldValue('scoreCustomerType', '');
      setFieldValue('scoreCustomerOrganizationName', '');
      clearFieldError('scoreCustomerType');
      clearFieldError('scoreCustomerOrganizationName');
    }
    validateField('userTypeId', roleId, {
      selectedUserTypeName: role?.typeName,
    });

    if (role?.typeName !== 'Service Provider') {
      setFieldValue('serviceProviderType', '');
      clearFieldError('serviceProviderType');
    }
  };

  const handleServiceProviderTypeChange = (event) => {
    const value = event.target.value;
    setFieldValue('serviceProviderType', value);
    setFieldTouched('serviceProviderType');
    validateField('serviceProviderType', value, {
      selectedUserTypeName: selectedUserType?.typeName,
    });
  };

  const handleEmailChange = (event) => {
    const value = event.target.value.trim();
    setFieldValue('email', value);
    setFieldTouched('email');
    validateField('email', value);
  };

  const handleEmailBlur = async () => {
    setFieldTouched('email');

    const email = formData.email;
    if (!email) {
      return;
    }

    try {
      await step1schema.validateAt('email', buildValidationModel({ email }));
    } catch (error) {
      setFieldError('email', error.message);
      return;
    }

    clearFieldError('email');

    const response = await validateEmailApi({ email });
    if (response.statusCode !== 200 && response.statusCode !== 201) {
      setFieldError('email', response.message || 'We could not validate this email.');
    }
  };

  const handlePhoneChange = (event) => {
    // Phone length is uniform across the platform (digits only, fixed range).
    const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, PHONE_MAX_LENGTH);

    setFieldValue('phoneNumber', digitsOnly);
    setFieldTouched('phoneNumber');
    validateField('phoneNumber', digitsOnly);

    // Show OTP section once the number is within the accepted length range.
    if (digitsOnly.length >= PHONE_MIN_LENGTH && digitsOnly.length <= PHONE_MAX_LENGTH) {
      setShowOtp(true);
    } else {
      setShowOtp(false);
      setOtpInputsVisible(false);
      setTimerSeconds(0);
    }

    if (formData.otpVerified) {
      setFieldValue('otpVerified', false);
      setFieldValue('termsAndPolicies', false, 'checkbox');
      setOtpInputsVisible(false);
      setOtp(Array(OTP_LENGTH).fill(''));
      setTimerSeconds(0);
    }
  };

  const handleCountryCodeChange = (event) => {
    const value = event.target.value;
    setFieldValue('countryCode', value);
    setFieldTouched('countryCode');
    validateField('countryCode', value);

    if (formData.otpVerified) {
      setFieldValue('otpVerified', false);
      setFieldValue('termsAndPolicies', false, 'checkbox');
      setShowOtp(false);
      setOtpInputsVisible(false);
      setOtp(Array(OTP_LENGTH).fill(''));
      setTimerSeconds(0);
    }
  };

  const handleTermsChange = (event) => {
    setFieldValue('termsAndPolicies', event.target.checked, 'checkbox');
    setFieldTouched('termsAndPolicies');
    validateField('termsAndPolicies', event.target.checked);
  };

  const handleSendOtp = async () => {
    try {
      await step4Schema.validate(
        {
          phoneNumber: formData.phoneNumber,
          countryCode: formData.countryCode,
        },
        { abortEarly: false }
      );

      setLoading(true);
      const response = await eklinkSignupSendOtp({
        phoneNumber: formData.phoneNumber,
        countryCode: formData.countryCode,
      });

      if (response.statusCode === 200) {
        setShowOtp(true);
        setOtpInputsVisible(true);
        setTimerSeconds(180); // 3 minutes
        setOtp(Array(OTP_LENGTH).fill(''));
        clearFieldError('phoneNumber');
        clearFieldError('otp');
        showFeedback('success', 'OTP sent successfully. Enter the code sent to your phone.');
        setLoading(false);
      } else {
        showFeedback('error', response.message || 'Unable to send OTP right now.');
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      if (error.inner) {
        const validationErrors = {};
        error.inner.forEach((item) => {
          validationErrors[item.path] = item.message;
        });
        setErrors((currentErrors) => ({ ...currentErrors, ...validationErrors }));
      } else {
        showFeedback('error', 'Unable to send OTP right now.');
      }
    }
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) {
      return;
    }

    setOtp((currentOtp) => {
      const nextOtp = [...currentOtp];
      nextOtp[index] = value;
      return nextOtp;
    });

    if (value && index < otp.length - 1) {
      const nextInput = document.getElementById(`signup-otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      const previousInput = document.getElementById(`signup-otp-${index - 1}`);
      previousInput?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join('');

    try {
      await step5Schema.validate({ otp: otpValue }, { abortEarly: false });

      setLoading(true);
      const response = await eklinkSignupVerifyOtp({
        otp: otpValue,
        phoneNumber: formData.phoneNumber,
        countryCode: formData.countryCode,
      });

      if (response.statusCode === 200 || response.statusCode === 201) {
        showFeedback('success', 'Phone number verified.');
        setFieldValue('otpVerified', true);
        setShowOtp(false);
        setOtpInputsVisible(false);
        setOtp(Array(OTP_LENGTH).fill(''));
        setTimerSeconds(0);
        setLoading(false);
      } else {
        setFieldError('otp', response.message || 'Unable to verify OTP.');
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      if (error.inner) {
        setFieldError('otp', error.inner[0]?.message || 'OTP is required.');
      } else {
        showFeedback('error', 'Unable to verify OTP right now.');
      }
    }
  };

  const handleContinueClick = async (event) => {
    event.preventDefault();

    const requiredFields = [
      'registrationType',
      'administrativeRegion',
      'userTypeId',
      'email',
      'countryCode',
      'phoneNumber',
      'termsAndPolicies',
    ];

    if (registrationType === 'organization') {
      requiredFields.push('organisationName');
    } else {
      requiredFields.push('firstName', 'lastName');
    }

    if (selectedUserType?.typeName === 'Service Provider') {
      requiredFields.push('serviceProviderType');
    }

    if (isScoreCustomer) {
      requiredFields.push('scoreCustomerType');
      if (registrationType !== 'organization') {
        requiredFields.push('scoreCustomerOrganizationName');
      }
    }

    setTouched((currentTouched) => ({
      ...currentTouched,
      ...requiredFields.reduce(
        (fieldState, fieldName) => ({ ...fieldState, [fieldName]: true }),
        {}
      ),
    }));

    const isValid = await validateStep();
    if (!isValid) {
      return;
    }

    setLoading(true);
    try {
      const response = await validationApi({
        countryCode: formData.countryCode,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
      });

      if (response.statusCode === 200 || response.statusCode === 201) {
        handleContinue();
        return;
      }

      showFeedback(
        'error',
        response.message || 'We could not validate your details.'
      );
    } catch (error) {
      showFeedback('error', 'We could not validate your details.');
    } finally {
      setLoading(false);
    }
  };

  const renderFieldLabel = (label, required = false) => (
    <Typography variant="subtitle2" sx={fieldLabelStyles}>
      {label}
      {required ? (
        <Box component="span" sx={{ color: 'error.main' }}>
          {' '}
          *
        </Box>
      ) : null}
    </Typography>
  );

  const renderDocumentLink = (label, url) =>
    url ? (
      <Link
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ color: '#294190', fontWeight: 700, textDecorationColor: '#294190' }}
      >
        {label}
      </Link>
    ) : (
      <Box component="span" sx={{ color: '#294190', fontWeight: 700 }}>
        {label}
      </Box>
    );

  return (
    <>
      <Stack spacing={4} sx={{ maxWidth: 760, mx: 'auto' }}>
        <Box sx={{ width: '100%', px: { xs: 0, md: 1 }, pb: 1 }}>
          <SignupProgress steps={signupFlowSteps} activeStep={0} />
        </Box>

        <Paper
          elevation={0}
          sx={{
            px: { xs: 2.5, sm: 6, md: 8 },
            py: { xs: 2.5, sm: 6, md: 8 },
            borderRadius: 4,
            backgroundColor: '#ffffff',
            boxShadow: '0 18px 48px rgba(28, 27, 31, 0.10)',
            border: '1px solid #E4E6F1',
          }}
        >
          <Box sx={{ maxWidth: 560, mx: 'auto' }}>
            <Stack spacing={{ xs: 2.5, sm: 2 }}>
              <Stack spacing={1} alignItems="center" textAlign="center">
                <Typography
                  variant="h4"
                  sx={authHeadingStyles}
                >
                  Let&apos;s Get Started
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={authSubtextStyles}
                >
                  Tell us about yourself
                </Typography>
              </Stack>

              <Box sx={fieldGroupStyles}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: 'text.primary', fontWeight: 700 }}
                >
                  I am registering as <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                    width: '100%',
                  }}
                >
                  {registrationOptions.map((option) => {
                    const isSelected = registrationType === option.value;

                    return (
                      <Box key={option.value}>
                        <Card
                          variant="outlined"
                          sx={{
                            width: '100%',
                            borderColor: isSelected
                              ? 'primary.main'
                              : '#E4E6F1',
                            backgroundColor: isSelected
                              ? 'rgba(90, 143, 17, 0.06)'
                              : 'background.paper',
                            transition: 'all 180ms ease',
                          }}
                        >
                          <CardActionArea
                            onClick={() => handleRegistrationChange(option.value)}
                            data-testid={`signup-registration-${option.value}`}
                            aria-pressed={isSelected}
                            sx={{
                              minHeight: 92,
                              py: { xs: 2, sm: 2.5 },
                              px: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                            }}
                          >
                            <Box sx={{ color: isSelected ? 'primary.main' : 'text.secondary' }}>
                              {option.icon}
                            </Box>
                            <Typography variant="subtitle2">{option.label}</Typography>
                          </CardActionArea>
                        </Card>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={fieldGroupStyles}>
                {renderFieldLabel('Country', true)}
                <TextField
                  select
                  value={formData.administrativeRegion || ''}
                  onChange={handleCountryChange}
                  error={touched.administrativeRegion && Boolean(errors.administrativeRegion)}
                  helperText={
                    touched.administrativeRegion && errors.administrativeRegion
                      ? errors.administrativeRegion
                      : undefined
                  }
                  SelectProps={{
                    displayEmpty: true,
                    IconComponent: KeyboardArrowDownRounded,
                  }}
                >
                  <MenuItem value="" disabled>
                    Select country
                  </MenuItem>
                  {countryOptions.map((country) => (
                    <MenuItem key={country.countryManagement.id} value={country.countryManagement.id}>
                      {country.countryManagement.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={fieldGroupStyles}>
                {renderFieldLabel('My role is', true)}
                <TextField
                  select
                  value={formData.userTypeId || ''}
                  onChange={handleRoleChange}
                  error={touched.userTypeId && Boolean(errors.userTypeId)}
                  helperText={
                    touched.userTypeId && errors.userTypeId
                      ? errors.userTypeId
                      : undefined
                  }
                  SelectProps={{
                    displayEmpty: true,
                    IconComponent: KeyboardArrowDownRounded,
                  }}
                >
                  <MenuItem value="" disabled>
                    Select user type
                  </MenuItem>
                  {availableUserTypes.map((userType) => (
                    <MenuItem key={userType.id} value={userType.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RoleIcon iconUrl={userType.iconUrl} />
                        <span>{signupPersonaLabel(userType.typeName)}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {selectedUserType?.typeName === 'Service Provider' && (
                <Box sx={fieldGroupStyles}>
                  {renderFieldLabel('Service provider type', true)}
                  <TextField
                    select
                    value={formData.serviceProviderType || ''}
                    onChange={handleServiceProviderTypeChange}
                    error={
                      touched.serviceProviderType && Boolean(errors.serviceProviderType)
                    }
                    helperText={
                      touched.serviceProviderType && errors.serviceProviderType
                        ? errors.serviceProviderType
                        : undefined
                    }
                    SelectProps={{
                      displayEmpty: true,
                      IconComponent: KeyboardArrowDownRounded,
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select service provider type
                    </MenuItem>
                    {serviceProviderTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}

              {isScoreCustomer && (
                <Box sx={fieldGroupStyles}>
                  {renderFieldLabel('Organisation type', true)}
                  <TextField
                    select
                    value={formData.scoreCustomerType || ''}
                    onChange={handleTextFieldChange('scoreCustomerType')}
                    inputProps={{ 'data-testid': 'signup-score-customer-type' }}
                    error={touched.scoreCustomerType && Boolean(errors.scoreCustomerType)}
                    helperText={
                      touched.scoreCustomerType && errors.scoreCustomerType
                        ? errors.scoreCustomerType
                        : undefined
                    }
                    SelectProps={{
                      displayEmpty: true,
                      IconComponent: KeyboardArrowDownRounded,
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select organisation type
                    </MenuItem>
                    {SCORE_CUSTOMER_TYPES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}

              {isScoreCustomer && registrationType === 'individual' && (
                <Box sx={fieldGroupStyles}>
                  {renderFieldLabel('Organisation name', true)}
                  <TextField
                    name="scoreCustomerOrganizationName"
                    placeholder="The organisation you represent"
                    value={formData.scoreCustomerOrganizationName || ''}
                    onChange={handleTextFieldChange('scoreCustomerOrganizationName')}
                    autoComplete="organization"
                    error={
                      touched.scoreCustomerOrganizationName &&
                      Boolean(errors.scoreCustomerOrganizationName)
                    }
                    helperText={
                      touched.scoreCustomerOrganizationName && errors.scoreCustomerOrganizationName
                        ? errors.scoreCustomerOrganizationName
                        : undefined
                    }
                  />
                </Box>
              )}

              {registrationType === 'individual' ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                    width: '100%',
                  }}
                >
                  <Box sx={{ ...fieldGroupStyles, width: '100%' }}>
                    {renderFieldLabel('First Name', true)}
                    <TextField
                      name="firstName"
                      placeholder="Enter your first name"
                      value={formData.firstName || ''}
                      onChange={handleTextFieldChange('firstName')}
                      autoComplete="given-name"
                      fullWidth
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={
                        touched.firstName && errors.firstName
                          ? errors.firstName
                          : undefined
                      }
                    />
                  </Box>
                  <Box sx={{ ...fieldGroupStyles, width: '100%' }}>
                    {renderFieldLabel('Last Name', true)}
                    <TextField
                      name="lastName"
                      placeholder="Enter your last name"
                      value={formData.lastName || ''}
                      onChange={handleTextFieldChange('lastName')}
                      autoComplete="family-name"
                      fullWidth
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={
                        touched.lastName && errors.lastName
                          ? errors.lastName
                          : undefined
                      }
                    />
                  </Box>
                </Box>
              ) : (
                <Box sx={fieldGroupStyles}>
                  {renderFieldLabel('Organization Name', true)}
                  <TextField
                    name="organisationName"
                    placeholder="Enter your organization name"
                    value={formData.organisationName || ''}
                    onChange={handleTextFieldChange('organisationName')}
                    autoComplete="organization"
                    error={touched.organisationName && Boolean(errors.organisationName)}
                    helperText={
                      touched.organisationName && errors.organisationName
                        ? errors.organisationName
                        : undefined
                    }
                  />
                </Box>
              )}

              <Box sx={fieldGroupStyles}>
                {renderFieldLabel('Email', true)}
                <TextField
                  placeholder="your@email.com"
                  value={formData.email || ''}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  inputProps={{ 'data-testid': 'signup-email' }}
                  error={touched.email && Boolean(errors.email)}
                  helperText={
                    touched.email && errors.email ? errors.email : undefined
                  }
                />
              </Box>

              <Stack spacing={1.5}>
                {renderFieldLabel('Phone', true)}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'minmax(116px, 136px) minmax(0, 1fr)',
                    },
                    gap: 1.5,
                    width: '100%',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      select
                      fullWidth
                      value={formData.countryCode || ''}
                      onChange={handleCountryCodeChange}
                      error={touched.countryCode && Boolean(errors.countryCode)}
                      SelectProps={{
                        IconComponent: KeyboardArrowDownRounded,
                        renderValue: (selectedValue) => {
                          const selectedCountry = countryOptions.find(
                            (country) =>
                              String(country.phoneCode) === String(selectedValue)
                          );

                          if (!selectedCountry) {
                            return selectedValue || '';
                          }

                          return (
                            <CountryPhoneCodeOption
                              country={selectedCountry}
                              fallbackLabel={selectedValue}
                            />
                          );
                        },
                      }}
                    >
                      {countryOptions.map((country) => (
                        <MenuItem key={country.countryManagement.id} value={String(country.countryManagement.phoneCode)}>
                          <CountryPhoneCodeMenuOption country={country.countryManagement} />
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      fullWidth
                      placeholder="Enter your phone number"
                      value={formData.phoneNumber || ''}
                      onChange={handlePhoneChange}
                      error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                      inputProps={{ 'aria-describedby': 'signup-phone-digit-count' }}
                    />
                  </Box>
                </Box>

                <Stack spacing={1} alignItems="flex-end">
                  {(formData.phoneNumber || '').length > 0 && (
                    <Typography
                      id="signup-phone-digit-count"
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: '100%' }}
                    >
                      {formData.phoneNumber.length === 1
                        ? '1 digit entered'
                        : `${formData.phoneNumber.length} digits entered`}
                    </Typography>
                  )}

                  {errors.phoneNumber && touched.phoneNumber && (
                    <Typography
                      variant="caption"
                      sx={{ width: '100%', color: 'error.main' }}
                    >
                      {errors.phoneNumber}
                    </Typography>
                  )}

                  {formData.otpVerified && (
                    <Typography
                      variant="caption"
                      sx={{ width: '100%', color: 'primary.main', fontWeight: 700 }}
                    >
                      Phone number verified
                    </Typography>
                  )}
                </Stack>

                <Collapse in={showOtp}>
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 0.5,
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: 3,
                      borderColor: 'rgba(90, 143, 17, 0.28)',
                      backgroundColor: 'rgba(90, 143, 17, 0.04)',
                    }}
                  >
                    {!otpInputsVisible ? (
                      <Button
                        variant="contained"
                        onClick={handleSendOtp}
                        disabled={loading}
                        fullWidth
                      >
                        Send OTP
                      </Button>
                    ) : (
                      <Stack spacing={2}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            width: otpRowWidth,
                            maxWidth: '100%',
                            alignSelf: 'center',
                            textAlign: 'left',
                          }}
                        >
                          Enter the {OTP_LENGTH}-digit code
                        </Typography>
                        <Stack spacing={2} alignItems="center">
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: `repeat(${OTP_LENGTH}, 42px)`,
                                sm: `repeat(${OTP_LENGTH}, 52px)`,
                              },
                              gap: 2,
                              width: otpRowWidth,
                            }}
                          >
                            {otp.map((digit, index) => (
                              <TextField
                                key={index}
                                id={`signup-otp-${index}`}
                                fullWidth={false}
                                value={digit}
                                onChange={(event) => handleOtpChange(index, event.target.value)}
                                onKeyDown={(event) => handleOtpKeyDown(index, event)}
                                inputProps={{
                                  inputMode: 'numeric',
                                  maxLength: 1,
                                  style: { textAlign: 'center' },
                                }}
                                sx={{
                                  width: { xs: 42, sm: 52 },
                                  '& .MuiOutlinedInput-root': {
                                    height: { xs: 46, sm: 54 },
                                  },
                                }}
                              />
                            ))}
                          </Box>
                          <Button
                            variant="contained"
                            onClick={handleVerifyOtp}
                            disabled={loading}
                            sx={{ width: otpRowWidth, maxWidth: '100%' }}
                          >
                            Verify OTP
                          </Button>
                        </Stack>

                        <Stack spacing={1} alignItems="center">
                          {timerSeconds > 0 ? (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Resend OTP in {formatTimer(timerSeconds)}
                            </Typography>
                          ) : (
                            <Button
                              variant="text"
                              onClick={handleSendOtp}
                              disabled={loading}
                              sx={{ px: 0, minHeight: 'unset' }}
                            >
                              Resend OTP
                            </Button>
                          )}
                        </Stack>

                        {errors.otp && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'error.main',
                              width: otpRowWidth,
                              maxWidth: '100%',
                              alignSelf: 'center',
                            }}
                          >
                            {errors.otp}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Paper>
                </Collapse>
              </Stack>

              <Divider flexItem />

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(formData.termsAndPolicies)}
                      onChange={handleTermsChange}
                      disabled={!formData.otpVerified}
                      inputProps={{ 'data-testid': 'signup-terms' }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      I agree to {renderDocumentLink('Terms and Conditions', termsUrl)}.
                    </Typography>
                  }
                  sx={{
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'center',
                    m: 0,
                    maxWidth: '100%',
                  }}
                />
              </Box>

              {touched.termsAndPolicies && errors.termsAndPolicies && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: -2 }}>
                  {errors.termsAndPolicies}
                </Typography>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={handleContinueClick}
                disabled={!formData.otpVerified || !formData.termsAndPolicies || loading}
                data-testid="signup-continue"
                sx={{ mt: 1, py: 1.5 }}
              >
                Continue
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Stack>

      <Snackbar
        open={Boolean(feedback?.open)}
        autoHideDuration={4000}
        onClose={closeFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={feedback?.severity || 'info'}
          variant="filled"
          onClose={closeFeedback}
          sx={{ width: '100%' }}
        >
          {feedback?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
